// Fichier: src/pages/accounting/JournalEntryForm.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, TextField, Button, IconButton,
  Table, TableHead, TableRow, TableCell, TableBody, Autocomplete,
  Snackbar, Alert, Chip, CircularProgress
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { db } from '../../firebase';
import { collection, doc, getDoc, getDocs, query, where, orderBy, setDoc } from 'firebase/firestore';

function toCents(n) {
  return Math.round((Number(n) || 0) * 100);
}

function newLine() {
  return { key: Math.random().toString(36).slice(2), account: null, lineLabel: '', debit: '', credit: '' };
}

const emptyEntry = () => ({
  date: new Date().toISOString().slice(0, 10),
  description: '',
  lines: [newLine(), newLine()]
});

const JournalEntryForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [entry, setEntry] = useState(emptyEntry());
  const [existingStatus, setExistingStatus] = useState(null);
  const [existingReference, setExistingReference] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  // Comptes actifs, pour l'autocomplete
  useEffect(() => {
    const load = async () => {
      const snap = await getDocs(query(collection(db, 'accounting_accounts'), orderBy('code')));
      setAccounts(snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((a) => a.active !== false));
    };
    load();
  }, []);

  // Charger l'écriture existante si édition
  useEffect(() => {
    if (!isEditing) return;
    const load = async () => {
      const snap = await getDoc(doc(db, 'accounting_entries', id));
      if (!snap.exists()) {
        setSnackbar({ open: true, message: 'Écriture introuvable.', severity: 'error' });
        setLoading(false);
        return;
      }
      const data = snap.data();
      setExistingStatus(data.status);
      setExistingReference(data.reference);
      setEntry({
        date: data.date?.toDate ? data.date.toDate().toISOString().slice(0, 10) : data.date,
        description: data.description || '',
        lines: (data.lines || []).map((l) => ({
          key: Math.random().toString(36).slice(2),
          account: { id: l.accountId, code: l.accountCode, label: l.accountLabel },
          lineLabel: l.lineLabel || '',
          debit: l.debit ? String(l.debit) : '',
          credit: l.credit ? String(l.credit) : ''
        }))
      });
      setLoading(false);
    };
    load();
  }, [id, isEditing]);

  const isReadOnly = existingStatus === 'validé';

  const totalDebit = entry.lines.reduce((sum, l) => sum + (Number(l.debit) || 0), 0);
  const totalCredit = entry.lines.reduce((sum, l) => sum + (Number(l.credit) || 0), 0);
  const isBalanced = toCents(totalDebit) === toCents(totalCredit) && toCents(totalDebit) > 0;

  const updateLine = (key, patch) => {
    setEntry((e) => ({
      ...e,
      lines: e.lines.map((l) => (l.key === key ? { ...l, ...patch } : l))
    }));
  };

  const addLine = () => setEntry((e) => ({ ...e, lines: [...e.lines, newLine()] }));

  const removeLine = (key) =>
    setEntry((e) => ({
      ...e,
      lines: e.lines.length > 2 ? e.lines.filter((l) => l.key !== key) : e.lines
    }));

  const validateForSave = () => {
    if (!entry.date) return 'La date est obligatoire.';
    if (!entry.description.trim()) return 'La description est obligatoire.';
    const filledLines = entry.lines.filter((l) => l.account && (Number(l.debit) > 0 || Number(l.credit) > 0));
    if (filledLines.length < 2) return 'Il faut au moins deux lignes avec un compte et un montant.';
    for (const l of entry.lines) {
      if (l.account && Number(l.debit) > 0 && Number(l.credit) > 0) {
        return 'Une ligne ne peut pas avoir un montant au débit ET au crédit.';
      }
      if ((Number(l.debit) > 0 || Number(l.credit) > 0) && !l.account) {
        return 'Chaque ligne avec un montant doit avoir un compte.';
      }
    }
    return null;
  };

  const ensureExercice = async (year) => {
    const exId = String(year);
    const exRef = doc(db, 'accounting_exercices', exId);
    const exSnap = await getDoc(exRef);
    if (!exSnap.exists()) {
      await setDoc(exRef, {
        year,
        label: `Exercice ${year}`,
        startDate: new Date(year, 0, 1),
        endDate: new Date(year, 11, 31),
        status: 'open',
        createdAt: new Date()
      });
    }
    return exId;
  };

  const generateReference = async (exerciceId) => {
    const snap = await getDocs(query(collection(db, 'accounting_entries'), where('exerciceId', '==', exerciceId)));
    const n = snap.size + 1;
    return `${exerciceId}-${String(n).padStart(4, '0')}`;
  };

  const handleSave = async (validateNow) => {
    const error = validateForSave();
    if (error) {
      setSnackbar({ open: true, message: error, severity: 'error' });
      return;
    }
    if (validateNow && !isBalanced) {
      setSnackbar({ open: true, message: 'Le débit et le crédit doivent être égaux pour valider.', severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      const dateObj = new Date(entry.date + 'T00:00:00');
      const year = dateObj.getFullYear();
      const exerciceId = await ensureExercice(year);

      const lines = entry.lines
        .filter((l) => l.account && (Number(l.debit) > 0 || Number(l.credit) > 0))
        .map((l) => ({
          accountId: l.account.id,
          accountCode: l.account.code,
          accountLabel: l.account.label,
          lineLabel: l.lineLabel?.trim() || l.account.label,
          debit: Number(l.debit) || 0,
          credit: Number(l.credit) || 0
        }));

      const basePayload = {
        date: dateObj,
        description: entry.description.trim(),
        exerciceId,
        lines,
        totalDebit: lines.reduce((s, l) => s + l.debit, 0),
        totalCredit: lines.reduce((s, l) => s + l.credit, 0),
        status: validateNow ? 'validé' : 'brouillon',
        updatedAt: new Date()
      };

      if (isEditing) {
        await setDoc(doc(db, 'accounting_entries', id), basePayload, { merge: true });
      } else {
        const reference = await generateReference(exerciceId);
        await setDoc(
          doc(collection(db, 'accounting_entries')),
          { ...basePayload, source: 'manuel', reference, createdAt: new Date() },
          { merge: true }
        );
      }
      navigate('/accounting/journal');
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: "Erreur lors de l'enregistrement.", severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={() => navigate('/accounting/journal')} aria-label="Retour">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          {isEditing ? `Écriture ${existingReference || ''}` : 'Nouvelle écriture'}
        </Typography>
        {isReadOnly && <Chip label="Validée — lecture seule" color="success" size="small" sx={{ ml: 1 }} />}
      </Box>

      {isReadOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Cette écriture est validée et non modifiable. Dévalide-la depuis le livre journal si tu dois la corriger.
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Date"
            type="date"
            value={entry.date}
            onChange={(e) => setEntry((f) => ({ ...f, date: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            disabled={isReadOnly}
            sx={{ minWidth: 200 }}
          />
          <TextField
            label="Description"
            value={entry.description}
            onChange={(e) => setEntry((f) => ({ ...f, description: e.target.value }))}
            disabled={isReadOnly}
            fullWidth
            sx={{ flex: 1, minWidth: 260 }}
          />
        </Box>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Compte</TableCell>
              <TableCell>Libellé de ligne</TableCell>
              <TableCell align="right">Débit (€)</TableCell>
              <TableCell align="right">Crédit (€)</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {entry.lines.map((l) => (
              <TableRow key={l.key}>
                <TableCell sx={{ minWidth: 260 }}>
                  <Autocomplete
                    size="small"
                    disabled={isReadOnly}
                    options={accounts}
                    getOptionLabel={(a) => (a ? `${a.code} — ${a.label}` : '')}
                    value={l.account}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    onChange={(e, val) => updateLine(l.key, { account: val })}
                    renderInput={(params) => <TextField {...params} placeholder="Rechercher un compte..." />}
                  />
                </TableCell>
                <TableCell sx={{ minWidth: 180 }}>
                  <TextField
                    size="small"
                    fullWidth
                    disabled={isReadOnly}
                    value={l.lineLabel}
                    placeholder={l.account?.label || ''}
                    onChange={(e) => updateLine(l.key, { lineLabel: e.target.value })}
                  />
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 110 }}>
                  <TextField
                    size="small"
                    type="number"
                    disabled={isReadOnly}
                    value={l.debit}
                    onChange={(e) => updateLine(l.key, { debit: e.target.value, credit: e.target.value ? '' : l.credit })}
                    inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }}
                  />
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 110 }}>
                  <TextField
                    size="small"
                    type="number"
                    disabled={isReadOnly}
                    value={l.credit}
                    onChange={(e) => updateLine(l.key, { credit: e.target.value, debit: e.target.value ? '' : l.debit })}
                    inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }}
                  />
                </TableCell>
                <TableCell>
                  {!isReadOnly && (
                    <IconButton size="small" onClick={() => removeLine(l.key)} disabled={entry.lines.length <= 2}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {!isReadOnly && (
          <Button startIcon={<AddIcon />} onClick={addLine} sx={{ mt: 1 }}>
            Ajouter une ligne
          </Button>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 4, mt: 3, pt: 2, borderTop: '1px solid #eee' }}>
          <Typography>Total débit : <strong>{totalDebit.toFixed(2)} €</strong></Typography>
          <Typography>Total crédit : <strong>{totalCredit.toFixed(2)} €</strong></Typography>
          <Chip label={isBalanced ? 'Équilibrée' : 'Non équilibrée'} color={isBalanced ? 'success' : 'warning'} size="small" />
        </Box>
      </Paper>

      {!isReadOnly && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant="outlined" disabled={saving} onClick={() => handleSave(false)}>
            Enregistrer en brouillon
          </Button>
          <Button variant="contained" disabled={saving || !isBalanced} onClick={() => handleSave(true)}>
            Enregistrer et valider
          </Button>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default JournalEntryForm;
